/**
 * InterviewRoom -- two-stage candidate flow:
 *   1. Lobby:  Tech-check (cam/mic), persona welcome card, "Start" button.
 *   2. Call:   Cinematic dark-mode room with persona-bubble + WS transcript.
 *
 * Phase 1.10a only ships the scaffold:
 *   - REST: GET /sessions/{id}/me  -> show context + lobby
 *   - REST: POST /sessions/{id}/start -> resume_token + ws_url
 *   - WS:   useInterviewSocket connects with envelope+heartbeat (text echo)
 *   - REST: POST /sessions/{id}/complete -> after "End interview"
 *
 * Phase 1.10b will:
 *   - Stream MediaRecorder audio chunks as `audio` frames
 *   - Receive `ai_audio` frames + drive the cinematic bubble's animation
 *   - Layer in chunked upload + browser-side recording mirror
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Loader2,
  AlertTriangle,
  Send,
  Wifi,
  WifiOff,
  PhoneOff,
  ShieldCheck,
  Clock,
  Video,
  VideoOff,
  Mic,
  MicOff,
  ChevronDown,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import { useInterviewSocket } from '../../hooks/useInterviewSocket';
import { useInterviewAudio } from '../../hooks/useInterviewAudio';
import { useInterviewRecorder } from '../../hooks/useInterviewRecorder';
import { useArtifactSync } from '../../hooks/useArtifactSync';
import CodingPane from '../../components/Interview/CodingPane';
import SystemDesignPane from '../../components/Interview/SystemDesignPane';
import NumbersPane from '../../components/Interview/NumbersPane';
import NotepadPane from '../../components/Interview/NotepadPane';
import TechCheck, { type TechCheckResult } from '../../components/Interview/TechCheck';
import PersonaBubble from '../../components/Interview/PersonaBubble';
import {
  buildInterviewWsUrl,
  completeSession,
  completeSessionByToken,
  fetchMySession,
  fetchSessionByToken,
  isPreScreenSession,
  isRecruiterDemoSession,
  startSession,
  startSessionByToken,
  type CandidateSessionMe,
  type SessionStartResponse,
} from '../../api/interview';
import { getAnonInviteToken } from '../../lib/anonInvite';

const RECRUITER_HUB_RESULTS = '/interviews?tab=results';

type Stage = 'loading' | 'lobby' | 'starting' | 'in_call' | 'completing' | 'completed' | 'abandoned' | 'error';

const InterviewRoom: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';
  const { isSignedIn, isLoading: authLoading, apiCall, getToken, authToken, authReady } = useAuth();

  // Anonymous warm-intro (Lite-ATS): the candidate reached the room via a
  // tokenized invite link, no Cevee account. The token (stored by the landing
  // page) authenticates REST + WS. When null this is the normal logged-in flow.
  const anonToken = useMemo(
    () => (sessionId ? getAnonInviteToken(sessionId) : null),
    [sessionId],
  );

  const [stage, setStage] = useState<Stage>('loading');
  const [me, setMe] = useState<CandidateSessionMe | null>(null);
  const [startResp, setStartResp] = useState<SessionStartResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tech, setTech] = useState<TechCheckResult | null>(null);

  // Shared camera/mic stream. Acquired in TechCheck, reused in CallStage so
  // the candidate doesn't get a second permission prompt. We own the cleanup.
  const streamRef = useRef<MediaStream | null>(null);
  const [streamReady, setStreamReady] = useState(false);

  // Optional screen-share stream (only used when the template requires it,
  // or for mocks which always force composite). Tracked as state because
  // the recorder hook needs to see it change (e.g. user revokes mid-call).
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  // Mirror of `screenStream` for teardown paths that can't read state: the
  // unmount cleanup and the `pagehide` listener both run outside of render.
  const screenStreamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    screenStreamRef.current = screenStream;
  }, [screenStream]);

  // Release every device we hold. Idempotent, so it is safe to call from both
  // the normal exit path and the browser's teardown events.
  const releaseMedia = useCallback(() => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((tr) => tr.stop()); } catch { /* ignore */ }
      streamRef.current = null;
    }
    if (screenStreamRef.current) {
      try { screenStreamRef.current.getTracks().forEach((tr) => tr.stop()); } catch { /* ignore */ }
      screenStreamRef.current = null;
    }
  }, []);

  const acquireStream = useCallback((stream: MediaStream) => {
    if (streamRef.current && streamRef.current !== stream) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
    }
    streamRef.current = stream;
    setStreamReady(true);
  }, []);

  const acquireScreenStream = useCallback((s: MediaStream | null) => {
    // The previous screen stream (if any) is owned by TechCheck and gets
    // stopped there when the user re-acquires. We just hold the latest.
    setScreenStream(s);
  }, []);

  // Stop tracks on unmount, and on `pagehide` as well: a browser-back can put
  // this page into the back/forward cache without ever running React cleanup,
  // which left the camera light and Chrome's "sharing your screen" bar on in a
  // tab the candidate had already navigated away from.
  useEffect(() => {
    window.addEventListener('pagehide', releaseMedia);
    return () => {
      window.removeEventListener('pagehide', releaseMedia);
      releaseMedia();
    };
  }, [releaseMedia]);

  // Load context once we're signed in.
  useEffect(() => {
    let mounted = true;
    if (authLoading) return;
    // Anonymous warm-intro skips the login gate -- the invite token is the
    // credential. Only logged-in (non-anon) sessions are bounced to /login.
    if (!isSignedIn && !anonToken) {
      navigate(`/login?next=${encodeURIComponent(`/interview/room/${sessionId}`)}`);
      return;
    }
    if (!sessionId) {
      setError('Missing session id');
      setStage('error');
      return;
    }
    (async () => {
      try {
        const data = anonToken
          ? await fetchSessionByToken(anonToken)
          : await fetchMySession(apiCall, sessionId);
        if (!mounted) return;
        setMe(data);
        // Short-circuit terminal states so the lobby never tries to start
        // something that can't be started.
        const st = data.session.status;
        if (st === 'completed') {
          setStage('completed');
        } else if (st === 'cancelled' || st === 'expired') {
          setError(
            st === 'cancelled'
              ? (lang === 'de' ? 'Diese Einladung wurde zurückgezogen.' : 'This invite was cancelled.')
              : (lang === 'de' ? 'Diese Einladung ist abgelaufen.' : 'This invite has expired.'),
          );
          setStage('error');
        } else if (st === 'in_progress') {
          // Candidate already entered the call once -- tab closed / hard
          // refresh while live. We block re-entry: a restart would wipe
          // the AI's transcript context (the WS would Replay nothing
          // since session_state would be re-seeded with transcript=[])
          // and lets candidates "rewind" bad answers. Show a clear
          // explanation + contact CTA instead.
          setStage('abandoned');
        } else {
          setStage('lobby');
        }
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : String(e));
        setStage('error');
      }
    })();
    return () => {
      mounted = false;
    };
  }, [authLoading, isSignedIn, anonToken, sessionId, apiCall, navigate]);

  const handleStart = useCallback(async () => {
    if (!sessionId) return;
    setStage('starting');
    setError(null);
    try {
      const resp = anonToken
        ? await startSessionByToken(anonToken)
        : await startSession(apiCall, sessionId);
      setStartResp(resp);
      setStage('in_call');
      // Jump back to the top of the page so the candidate isn't stuck
      // mid-scroll on the lobby content when the interview UI mounts.
      // We use a microtask so the in_call DOM is rendered first.
      requestAnimationFrame(() => {
        try {
          window.scrollTo({ top: 0, behavior: 'auto' });
        } catch {
          window.scrollTo(0, 0);
        }
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStage('lobby');
    }
  }, [apiCall, sessionId, anonToken]);

  const handleEnd = useCallback(async () => {
    if (!sessionId) return;
    setStage('completing');
    try {
      if (anonToken) {
        await completeSessionByToken(anonToken);
      } else {
        await completeSession(apiCall, sessionId);
      }
    } catch (e) {
      console.warn('complete failed', e);
    } finally {
      // Release camera/mic AND screen-share immediately on hang-up so
      // the browser's "is using your camera" + "is sharing your screen"
      // indicators clear right away instead of lingering on the
      // completed screen until the route unmounts.
      releaseMedia();
      setScreenStream(null);
      setStage('completed');
    }
  }, [apiCall, sessionId, anonToken, releaseMedia]);

  // Passport pre-screens auto-return to the Passport Pre-Screen tab a few
  // seconds after completion, so the candidate lands back where they started
  // without having to hit the button. Other contexts keep the manual button.
  useEffect(() => {
    if (stage !== 'completed') return;
    const isDemo = !!me?.is_recruiter_demo || isRecruiterDemoSession(me?.session);
    const isPreScreen = isPreScreenSession(me?.session) && !isDemo;
    if (!isPreScreen) return;
    const tmr = setTimeout(() => navigate('/?tab=passport&pp=prescreen'), 6000);
    return () => clearTimeout(tmr);
  }, [stage, me, navigate]);

  if (authLoading || stage === 'loading') {
    return <LoadingScreen />;
  }

  if (stage === 'error' || (!me && stage !== 'completed')) {
    return <ErrorScreen lang={lang} message={error || 'Unknown error'} onBack={() => navigate('/')} />;
  }

  if (stage === 'completed') {
    const isDemo = !!me?.is_recruiter_demo || isRecruiterDemoSession(me?.session);
    const isPreScreen = isPreScreenSession(me?.session) && !isDemo;
    const isMock = !!me?.session?.is_mock && !isDemo && !isPreScreen;
    const handleBack = () => {
      if (isDemo) {
        navigate(RECRUITER_HUB_RESULTS);
        return;
      }
      if (isPreScreen) {
        navigate('/?tab=passport&pp=prescreen');
        return;
      }
      navigate('/mock/history');
    };
    return (
      <ThankYouScreen
        lang={lang}
        isMock={isMock}
        isPreScreen={isPreScreen}
        isRecruiterDemo={isDemo}
        onBack={handleBack}
      />
    );
  }

  if (stage === 'lobby' || stage === 'starting') {
    // For mock interviews we treat the template's `enable_screen_share`
    // flag as a SOFT preference: the screen-share section is still
    // offered so the candidate can record their setup if they want,
    // but the Start button never waits for it -- otherwise mobile
    // users (iOS Safari has no `getDisplayMedia`) would be stuck.
    // Recruiter sessions keep the hard gate.
    const isMockSession = !!me?.session?.is_mock;
    const templateWantsScreen = !!me?.template?.enable_screen_share;
    const lobbyIsDemo = !!me?.is_recruiter_demo || isRecruiterDemoSession(me?.session);
    const lobbyIsPreScreen = isPreScreenSession(me?.session) && !lobbyIsDemo;
    const lobbyIsMock = isMockSession && !lobbyIsDemo && !lobbyIsPreScreen;
    return (
      <Lobby
        lang={lang}
        me={me!}
        starting={stage === 'starting'}
        error={error}
        tech={tech}
        onTechReady={setTech}
        onStreamReady={acquireStream}
        onScreenStreamReady={acquireScreenStream}
        requireScreenShare={!isMockSession && templateWantsScreen}
        offerScreenShare={templateWantsScreen}
        isPreScreen={lobbyIsPreScreen}
        isMock={lobbyIsMock}
        isRecruiterDemo={lobbyIsDemo}
        onStart={handleStart}
        streamReady={streamReady}
      />
    );
  }

  if (stage === 'abandoned') {
    const isDemo = !!me?.is_recruiter_demo || isRecruiterDemoSession(me?.session);
    const isPreScreen = isPreScreenSession(me?.session) && !isDemo;
    const isMock = !!me?.session?.is_mock && !isDemo && !isPreScreen;
    return (
      <AbandonedScreen
        lang={lang}
        isMock={isMock}
        isPreScreen={isPreScreen}
        isRecruiterDemo={isDemo}
        onMockHistory={() => navigate(isPreScreen ? '/?tab=passport&pp=prescreen' : '/mock/history')}
        onRecruiterHub={() => navigate(RECRUITER_HUB_RESULTS)}
      />
    );
  }

  // stage === 'in_call' | 'completing'
  const tplType = me?.template?.type || 'behavioral';
  // Panel toggles: prefer the explicit flags coming from the backend.
  // For old templates (pre-013 migration) we fall back to the legacy
  // type-based mapping so behaviour stays identical.
  const codingPanelOn =
    typeof me?.template?.enable_coding_panel === 'boolean'
      ? me!.template!.enable_coding_panel!
      : tplType === 'coding';
  const whiteboardPanelOn =
    typeof me?.template?.enable_whiteboard_panel === 'boolean'
      ? me!.template!.enable_whiteboard_panel!
      : tplType === 'system_design';
  // Numbers panel: always opt-in only (no type fallback). Recruiter sets
  // it explicitly per template for quantitative interviews (consulting
  // case, finance modelling, sales pipeline math).
  const numbersPanelOn = !!me?.template?.enable_numbers_panel;
  // Notepad panel: opt-in only, never inferred from `type`. Free-form
  // post-it scratch surface for STAR notes / assumptions / hypotheses.
  const notepadPanelOn = !!me?.template?.enable_notepad_panel;

  return (
    <CallStage
      lang={lang}
      sessionId={sessionId!}
      anonToken={anonToken}
      startResp={startResp!}
      codingPanelOn={codingPanelOn}
      whiteboardPanelOn={whiteboardPanelOn}
      numbersPanelOn={numbersPanelOn}
      notepadPanelOn={notepadPanelOn}
      templateLanguage={me?.template?.language || lang}
      getToken={getToken}
      authToken={authToken}
      authReady={authReady}
      apiCall={apiCall}
      ending={stage === 'completing'}
      onEnd={handleEnd}
      stream={streamRef.current}
      screenStream={screenStream}
      useComposite={!!me?.template?.enable_screen_share}
    />
  );
};

// ============================================================
// Lobby (light theme, Apple-clean, matches the rest of Cevee)
// ============================================================

const Lobby: React.FC<{
  lang: 'de' | 'en';
  me: CandidateSessionMe;
  starting: boolean;
  error: string | null;
  tech: TechCheckResult | null;
  streamReady: boolean;
  onTechReady: (r: TechCheckResult) => void;
  onStreamReady: (s: MediaStream) => void;
  onScreenStreamReady: (s: MediaStream | null) => void;
  requireScreenShare: boolean;
  /** Show the screen-share section even if it isn't required (mocks). */
  offerScreenShare?: boolean;
  /** Context framing: candidate-run Passport pre-screen. */
  isPreScreen?: boolean;
  /** Context framing: private practice simulation. */
  isMock?: boolean;
  /** Context framing: recruiter previewing their own template. */
  isRecruiterDemo?: boolean;
  onStart: () => void;
}> = ({ lang, me, starting, error, tech, streamReady, onTechReady, onStreamReady, onScreenStreamReady, requireScreenShare, offerScreenShare, isPreScreen = false, isMock = false, isRecruiterDemo = false, onStart }) => {
  const t = {
    badge:    lang === 'de' ? 'Cevee · AI Interview' : 'Cevee · AI Interview',
    title:    lang === 'de' ? 'Du bist gleich dran' : 'You are about to start',
    sub:      lang === 'de' ? 'Atme einmal durch - alles läuft in deinem Tempo.' : 'Take a breath - everything moves at your pace.',
    hostedBy: lang === 'de' ? 'Mit dir spricht' : 'Hosted by',
    duration: lang === 'de' ? 'Geschätzte Dauer' : 'Estimated duration',
    minutes:  lang === 'de' ? 'Minuten' : 'minutes',
    privacy:  lang === 'de'
      ? 'Aufzeichnung startet erst, wenn du auf "Interview starten" klickst.'
      : 'Recording only starts after you click "Start interview".',
    cta:      lang === 'de' ? 'Interview starten' : 'Start interview',
    starting: lang === 'de' ? 'Verbinde ...' : 'Connecting ...',
  };

  // Context banner: tells the candidate *which kind* of session this is and
  // who (if anyone) gets to see the result. Keeps Pre-Screen, Mock, Demo and
  // real recruiter interviews unmistakably distinct already in the lobby.
  const contextNote = (() => {
    if (isRecruiterDemo) {
      return {
        tone: 'demo' as const,
        title: lang === 'de' ? 'Template-Vorschau' : 'Template preview',
        body: lang === 'de'
          ? 'So erleben Kandidat:innen dieses Interview. Nur zum Testen.'
          : 'This is how candidates experience this interview. For testing only.',
      };
    }
    if (isPreScreen) {
      return {
        tone: 'prescreen' as const,
        title: lang === 'de' ? 'Cevee Intro' : 'Cevee Intro',
        body: lang === 'de'
          ? 'Du machst es selbst. Ergebnis, Transcript und Video bleiben privat - Recruiter:innen sehen sie nur, wenn du sie freigibst.'
          : 'You run it yourself. Result, transcript and video stay private - recruiters only see them if you choose to share.',
      };
    }
    if (isMock) {
      return {
        tone: 'mock' as const,
        title: lang === 'de' ? 'Übungs-Simulation' : 'Practice simulation',
        body: lang === 'de'
          ? 'Nur für dich. Niemand sonst sieht dieses Interview - reine Übung.'
          : 'Just for you. No one else sees this interview - pure practice.',
      };
    }
    return {
      tone: 'recruiter' as const,
      title: lang === 'de' ? 'Echtes Interview' : 'Real interview',
      body: me.company_name
        ? (lang === 'de'
            ? `Dein Ergebnis geht an das Recruiting-Team von ${me.company_name}.`
            : `Your result goes to the recruiting team at ${me.company_name}.`)
        : (lang === 'de'
            ? 'Dein Ergebnis geht an das Recruiting-Team.'
            : 'Your result goes to the recruiting team.'),
    };
  })();

  const noteStyles: Record<string, string> = {
    prescreen: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    mock:      'bg-sky-50 border-sky-200 text-sky-800',
    demo:      'bg-violet-50 border-violet-200 text-violet-800',
    recruiter: 'bg-amber-50 border-amber-200 text-amber-800',
  };

  const canStart =
    !!tech &&
    tech.audio &&
    tech.video &&
    (!requireScreenShare || tech.screen) &&
    streamReady &&
    !starting;

  // Small human-readable reason the Start button is locked. Helps the
  // candidate self-recover instead of staring at a greyed button.
  const blockedReason = (() => {
    if (canStart || starting) return null;
    if (!tech?.audio || !tech?.video) {
      return lang === 'de'
        ? 'Bitte Mikrofon und Kamera freigeben.'
        : 'Please enable microphone and camera.';
    }
    if (requireScreenShare && !tech?.screen) {
      return lang === 'de'
        ? 'Bitte Bildschirm freigeben - oben rechts in der Tech-Check-Karte.'
        : 'Please share your screen - top-right of the tech-check card.';
    }
    if (!streamReady) {
      return lang === 'de'
        ? 'Kamera wird vorbereitet ...'
        : 'Preparing camera ...';
    }
    return null;
  })();

  return (
    <div className="min-h-screen bg-slate-50/30">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-5">
        {/* Badge */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center shadow-[0_2px_6px_-1px_rgba(236,72,153,0.4)]">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-[11px] font-semibold tracking-wider uppercase text-pink-600">
            {t.badge}
          </span>
        </div>

        {/* Welcome card */}
        <div className="rounded-3xl bg-white border border-slate-200/70 shadow-[0_2px_8px_rgba(15,23,42,0.04)] overflow-hidden">
          {me.persona && (
            <div className={`h-28 bg-gradient-to-br ${me.persona.gradient || 'from-pink-500 to-orange-400'} flex items-center justify-center`}>
              <div className="w-16 h-16 rounded-full bg-white/25 backdrop-blur-md border border-white/40 flex items-center justify-center text-white text-xl font-bold tracking-tight shadow-inner">
                {(me.persona.name || '?').charAt(0)}
              </div>
            </div>
          )}
          <div className="p-6">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{t.title}</h1>
            <p className="text-slate-500 mt-0.5">{t.sub}</p>

            <div className={`mt-4 rounded-2xl border px-3.5 py-3 flex items-start gap-2.5 ${noteStyles[contextNote.tone]}`}>
              {contextNote.tone === 'recruiter' ? (
                <Send className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : contextNote.tone === 'demo' ? (
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : (
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider">{contextNote.title}</p>
                <p className="text-sm mt-0.5 leading-snug">{contextNote.body}</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Stat
                label={t.hostedBy}
                value={me.persona?.name || '--'}
                accent={
                  <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${me.persona?.gradient || 'from-slate-300 to-slate-500'}`} />
                }
              />
              <Stat
                label={t.duration}
                value={`~${me.template?.duration_minutes || 30} ${t.minutes}`}
                accent={<Clock className="w-4 h-4 text-slate-400" />}
              />
            </div>

            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {me.template?.family_key === 'cevee_prescreen_intro' ? 'Warm Intro' : me.template?.title}
              </p>
              {me.company_name && <p className="text-sm text-slate-700 mt-0.5">{me.company_name}</p>}
            </div>
          </div>
        </div>

        {/* Tech-check */}
        <TechCheck
          language={lang}
          onReady={onTechReady}
          onStreamReady={onStreamReady}
          onScreenStreamReady={onScreenStreamReady}
          requireScreenShare={requireScreenShare}
          offerScreenShare={offerScreenShare}
        />

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-2xl bg-red-50 border border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Footer / CTA */}
        <div className="rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span>{t.privacy}</span>
            </div>
            {blockedReason && (
              <div className="text-xs text-amber-600 font-medium">
                {blockedReason}
              </div>
            )}
          </div>
          <button
            disabled={!canStart}
            onClick={onStart}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {starting ? t.starting : t.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// In-call shell (dark cinematic, voice-AI vibe)
// ============================================================

const CallStage: React.FC<{
  lang: 'de' | 'en';
  sessionId: string;
  startResp: SessionStartResponse;
  codingPanelOn: boolean;
  whiteboardPanelOn: boolean;
  numbersPanelOn: boolean;
  notepadPanelOn: boolean;
  templateLanguage: string;
  anonToken: string | null;
  getToken: (opts?: { skipCache?: boolean }) => Promise<string | null>;
  authToken: string | null;
  authReady: boolean;
  apiCall: ReturnType<typeof useAuth>['apiCall'];
  ending: boolean;
  onEnd: () => void;
  stream: MediaStream | null;
  /**
   * Optional screen-share stream. Forwarded to the recorder so the
   * composite WebM contains the candidate's screen alongside the webcam.
   * Null means "screen share not currently active" -- the recorder
   * gracefully falls back to webcam-only.
   */
  screenStream: MediaStream | null;
  /**
   * Whether to use the canvas composite pipeline in the recorder. When
   * false the recorder stays on the cheap single-track path. Driven by
   * the template's `enable_screen_share` flag.
   */
  useComposite: boolean;
}> = ({
  lang,
  sessionId,
  startResp,
  codingPanelOn,
  whiteboardPanelOn,
  numbersPanelOn,
  notepadPanelOn,
  templateLanguage: _templateLanguage,
  anonToken,
  getToken,
  authToken,
  authReady,
  apiCall,
  ending,
  onEnd,
  stream,
  screenStream,
  useComposite,
}) => {
  const [draft, setDraft] = useState('');
  const [wsError, setWsError] = useState<string | null>(null);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  // Ref mirror: onCandidateSpeechState must not stop AI playback on
  // phantom echo VAD while the server still considers the AI turn active.
  const aiSpeakingRef = useRef(false);
  const [candidateSpeaking, setCandidateSpeaking] = useState(false);
  const [aiTextLive, setAiTextLive] = useState('');
  // Candidate's own live STT subtitle (mirrors aiTextLive). Display-only:
  // the authoritative line is committed via onCandidateText below.
  const [candidateTextLive, setCandidateTextLive] = useState('');
  // Brief toast surfaced after the WS reconnects and the AI has been
  // re-seeded with the prior transcript. Lets the candidate know
  // they didn't lose anything when the network blipped.
  const [resumedToast, setResumedToast] = useState<{ lines: number; expiresAt: number } | null>(null);
  const selfVideoRef = useRef<HTMLVideoElement | null>(null);

  // Bind self-view to the shared stream from the lobby.
  // Keep the <video> mounted when cam is toggled off (track.enabled=false);
  // remounting broke preview because this effect only depended on [stream].
  useEffect(() => {
    const el = selfVideoRef.current;
    if (!el || !stream) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    if (camOn) {
      void el.play().catch(() => { /* autoplay flake -- ignore */ });
    }
  }, [stream, camOn]);

  // Camera / mic toggle (track-level enable, browser LED stays on but feed is muted)
  useEffect(() => {
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => { t.enabled = camOn; });
  }, [stream, camOn]);
  useEffect(() => {
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => { t.enabled = micOn; });
  }, [stream, micOn]);

  // Guard against accidental tab close / refresh during the call. Browsers
  // ignore custom strings nowadays (security: they show a generic prompt)
  // but the prompt itself is what we need. We deliberately keep the guard
  // active during `ending` too -- the recorder needs up to 45s to flush
  // the last cluster + upload; closing the tab early truncates the replay.
  // Programmatic React Router navigation (handleEnd -> setStage('completed'))
  // never triggers beforeunload, so the clean-exit path is unaffected.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const wsUrl = useMemo(() => buildInterviewWsUrl(sessionId), [sessionId]);

  const getJwt = useCallback(async () => {
    // Anonymous warm-intro has no Cevee account -> no JWT (WS uses the
    // resume token alone, accepted by the backend's anonymous branch).
    if (anonToken) return null;
    // Prefer the token already in memory (same pool apiCall uses).
    if (authToken) return authToken;
    return (await getToken()) || null;
  }, [getToken, anonToken, authToken]);

  // Forward declaration so the audio hook can call sendAudio synchronously
  // without circular deps with the socket hook.
  const sendAudioRef = useRef<(b64: string) => boolean | void>(() => undefined);
  // Same ref-trick for the playback-buffered hint: we want to tell the
  // server how many ms of AI audio are still queued whenever a
  // response wraps, but `sendPlaybackBuffered` comes OUT of the socket
  // hook while `onAiSpeechState` is passed INTO it, so we route through
  // a ref to avoid the circular dep.
  const sendPlaybackBufferedRef = useRef<(ms: number) => boolean | void>(() => undefined);

  // Audio I/O: capture mic -> PCM16 -> ws; receive PCM16 -> playback.
  // Per-persona voice_speed: lets the backend ship a small per-voice
  // playback multiplier (e.g. Mira on coral reads slower than Lara on
  // marin, so coral gets 1.1x). Falls through to the hook's default
  // (1.0) when the persona omits the field. `startResp.persona` is
  // the slim payload from /sessions/start; the full Persona object
  // isn't in scope here, but startResp carries voice_speed alongside
  // the other persona fields the live room already needs.
  const { aiAmplitude, pushAudio, resetPlaybackHead, getBufferedPlaybackMs, stopAiPlayback, aiAudioContext, aiAudioTapNode } =
    useInterviewAudio({
      stream,
      enabled: true,
      micOn,
      sendAudio: (b64) => sendAudioRef.current(b64),
      voiceSpeed: startResp?.persona?.voice_speed ?? undefined,
    });

  // Recorder: WebM + audio mixed (candidate mic + AI playback) streamed to
  // Azure Blob via interview-service. Kicks off automatically once we have
  // both the camera stream and the AI playback ctx (so the first recording
  // chunk already contains AI audio).
  const recorder = useInterviewRecorder({
    sessionId,
    cameraStream: stream,
    aiAudioContext,
    aiAudioTapNode,
    apiCall,
    anonToken,
    enabled: !ending,
    useComposite,
    screenStream,
  });

  const {
    status,
    transcript,
    sendText,
    sendAudio,
    sendPlaybackBuffered,
    sendArtifact,
    sendArtifactEvent,
    endSession,
    forceReconnect,
  } = useInterviewSocket({
    wsUrl,
    resumeToken: startResp.resume_token,
    getJwt,
    anonymous: !!anonToken,
    // Logged-in: wait until auth bootstrap finished (token in memory).
    // Anonymous warm-intro uses resume_token only.
    enabled: !!anonToken || authReady,
    onSessionComplete: () => {
      // Server reported completion (AI/timer-initiated). Mirror the
      // manual-end path: show the completing UI, give the recorder
      // up to 45s to flush its final MediaRecorder cluster + upload
      // the last chunks + call /recording/finalize. Without this the
      // remuxer ends up with a truncated trailing WebM cluster and
      // mkvmerge falls back to first-segment-only -> 1-2 min replay.
      void (async () => {
        try {
          await Promise.race([
            recorder.stop(),
            new Promise<void>((resolve) => setTimeout(resolve, 45000)),
          ]);
        } finally {
          onEnd();
        }
      })();
    },
    onError: (code, message) => {
      // `reconnect_giveup` means we burned through all 7 reconnect
      // attempts (~2 min budget). Surface the code itself so the
      // banner-render can show a Retry button instead of a dead-end
      // error string.
      if (code === 'reconnect_giveup') {
        setWsError('reconnect_giveup');
      } else {
        setWsError(message ? `${code}: ${message}` : code);
      }
    },
    onAiAudio: (b64) => pushAudio(b64),
    onAiSpeechState: (s) => {
      const on = s === 'started';
      aiSpeakingRef.current = on;
      setAiSpeaking(on);
      if (s === 'stopped') {
        // Tell the server how many ms of AI audio are STILL queued in
        // the AudioContext after Azure's response.done. Without this,
        // the server's echo guard releases after a fixed 600ms tail
        // and any speaker -> mic bleed from the trailing audio wakes
        // up Azure VAD -> response gets cancelled -> the un-played
        // portion is lost ('AI bricht am ende ab' bug).
        const bufferedMs = getBufferedPlaybackMs();
        if (bufferedMs > 0) {
          sendPlaybackBufferedRef.current(bufferedMs);
        }
        resetPlaybackHead();
        setAiTextLive('');
      }
    },
    onCandidateSpeechState: (s) => {
      setCandidateSpeaking(s === 'started');
      // New candidate turn -> drop any leftover live subtitle. This also
      // clears the rare case where a turn was rejected as a hallucination
      // (no committed line arrives, so nothing else would clear it).
      if (s === 'started') setCandidateTextLive('');
      // Real barge-in only after the server's ai_speech:stopped. While
      // aiSpeakingRef is true, candidate_speech:started is usually
      // phantom echo -- stopping playback there sounded like the AI
      // cutting off mid-sentence.
      if (s === 'started' && !aiSpeakingRef.current) {
        stopAiPlayback();
        setAiSpeaking(false);
        aiSpeakingRef.current = false;
        setAiTextLive('');
      }
    },
    onCandidateTextDelta: (txt) => setCandidateTextLive((prev) => prev + txt),
    onCandidateText: () => setCandidateTextLive(''),
    onAiTextDelta: (txt) => setAiTextLive((prev) => prev + txt),
    onAiText: () => setAiTextLive(''),
    onAiTextDrop: () => {
      // Server-side hallucination filter fired: wipe both the live
      // bubble and the partial assistant transcript line that may
      // have already committed (we get the 'done' event from Azure
      // even when the response was cancelled mid-stream).
      setAiTextLive('');
    },
    onResumed: (lines) => {
      setResumedToast({ lines, expiresAt: Date.now() + 6000 });
    },
  });

  // Auto-dismiss the resumed toast.
  useEffect(() => {
    if (!resumedToast) return;
    const remaining = Math.max(0, resumedToast.expiresAt - Date.now());
    const t = setTimeout(() => setResumedToast(null), remaining);
    return () => clearTimeout(t);
  }, [resumedToast]);

  // Bind sendAudio after socket hook is established
  useEffect(() => {
    sendAudioRef.current = sendAudio;
  }, [sendAudio]);

  useEffect(() => {
    sendPlaybackBufferedRef.current = sendPlaybackBuffered;
  }, [sendPlaybackBuffered]);

  const handleSubmit = useCallback(() => {
    const txt = draft.trim();
    if (!txt) return;
    if (sendText(txt)) {
      setDraft('');
    }
  }, [draft, sendText]);

  const personaGradient = startResp.persona?.gradient || 'from-pink-500 to-orange-400';
  const isLive = status === 'open';
  const isReconnecting = status === 'reconnecting' || status === 'connecting' || status === 'authenticating';
  const bubbleStatus = isLive ? 'live' : isReconnecting ? 'reconnecting' : 'idle';

  // Artifact mode: split the screen with a Monaco / whiteboard pane so the
  // candidate can code or sketch while the AI listens and observes. Both
  // panels can be active at the same time (e.g. system-design + scratch
  // pad), in which case we show them as tabs in the artifact column.
  const hasArtifact = codingPanelOn || whiteboardPanelOn || numbersPanelOn || notepadPanelOn;
  type ArtifactTab = 'code' | 'canvas' | 'numbers' | 'notepad';
  const initialArtifactTab: ArtifactTab = codingPanelOn
    ? 'code'
    : whiteboardPanelOn
      ? 'canvas'
      : numbersPanelOn
        ? 'numbers'
        : 'notepad';
  const [activeArtifactTab, setActiveArtifactTab] = useState<ArtifactTab>(initialArtifactTab);
  useEffect(() => {
    // Auto-snap to the next available pane if the active one was disabled.
    // Order: code -> canvas -> numbers -> notepad.
    const fallback = (): ArtifactTab => {
      if (codingPanelOn) return 'code';
      if (whiteboardPanelOn) return 'canvas';
      if (numbersPanelOn) return 'numbers';
      return 'notepad';
    };
    if (activeArtifactTab === 'code' && !codingPanelOn) setActiveArtifactTab(fallback());
    else if (activeArtifactTab === 'canvas' && !whiteboardPanelOn) setActiveArtifactTab(fallback());
    else if (activeArtifactTab === 'numbers' && !numbersPanelOn) setActiveArtifactTab(fallback());
    else if (activeArtifactTab === 'notepad' && !notepadPanelOn) setActiveArtifactTab(fallback());
  }, [activeArtifactTab, codingPanelOn, whiteboardPanelOn, numbersPanelOn, notepadPanelOn]);

  // Code editor / canvas / numbers -> server (debounced, then server-side
  // throttled again to 8s before being relayed to Azure Realtime).
  const codeSync = useArtifactSync({
    kind: 'code',
    enabled: codingPanelOn && isLive,
    sendArtifact,
    debounceMs: 2500,
  });
  const canvasSync = useArtifactSync({
    kind: 'canvas',
    enabled: whiteboardPanelOn && isLive,
    sendArtifact,
    debounceMs: 2500,
  });
  const numbersSync = useArtifactSync({
    kind: 'numbers',
    enabled: numbersPanelOn && isLive,
    sendArtifact,
    debounceMs: 2500,
  });
  const notepadSync = useArtifactSync({
    kind: 'notepad',
    enabled: notepadPanelOn && isLive,
    sendArtifact,
    // Slightly longer debounce on the notepad: free-form notes are
    // typed in bursts, no need to push every 2.5s.
    debounceMs: 3500,
  });

  // Local "hangup in progress" state so the End button shows its
  // spinner the INSTANT the candidate clicks it -- before this, the
  // parent only flipped `ending=true` after onEnd() ran, which meant
  // the button looked dead during the (sometimes multi-second)
  // recorder-drain phase and candidates re-clicked, thinking it broke.
  // Doubles as a guard against double-clicks (recorder.stop is not
  // idempotent).
  const [hangingUp, setHangingUp] = useState(false);
  const hangingUpRef = useRef(false);
  const handleHangup = useCallback(async () => {
    if (hangingUpRef.current) return;
    hangingUpRef.current = true;
    setHangingUp(true);

    // 1) Flush any pending artifact edits BEFORE asking the server to
    //    complete the session -- otherwise the debounce timer may not
    //    have fired yet and the candidate's last edits never reach
    //    Redis, causing the review page to show an empty code panel.
    try { codeSync.flush(); } catch { /* noop */ }
    try { canvasSync.flush(); } catch { /* noop */ }
    try { numbersSync.flush(); } catch { /* noop */ }
    try { notepadSync.flush(); } catch { /* noop */ }
    if (codingPanelOn || whiteboardPanelOn || numbersPanelOn || notepadPanelOn) {
      await new Promise((r) => setTimeout(r, 150));
    }

    // 2) Tell the realtime backend the candidate is done talking.
    try { endSession(); } catch { /* noop */ }

    // 3) Stop the recorder + drain its upload queue. We let this run
    //    naturally -- the button is already in its loading state so
    //    the candidate sees something happening. A generous 45s
    //    ceiling acts as a last-resort safety net for the rare case
    //    where a chunk upload genuinely never resolves (offline /
    //    blob storage outage): better to proceed to the completion
    //    screen than trap the candidate on a spinning button forever.
    try {
      await Promise.race([
        recorder.stop(),
        new Promise<void>((resolve) => setTimeout(resolve, 45000)),
      ]);
    } catch { /* noop */ }

    // 4) Always proceed to onEnd, even if anything above failed.
    onEnd();
  }, [endSession, onEnd, recorder, codeSync, canvasSync, numbersSync, codingPanelOn, whiteboardPanelOn, numbersPanelOn]);

  // The bubble is "speaking" while audio is being streamed back; we use a
  // small amplitude floor so the eye sees motion even on quiet sibilants.
  const speaking = aiSpeaking || aiAmplitude > 0.05;

  return (
    <div className="min-h-screen bg-[#070713] text-white overflow-hidden relative">
      {/* Cosmic backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 30%, rgba(99, 102, 241, 0.08), transparent 55%),' +
            'radial-gradient(ellipse at 80% 80%, rgba(236, 72, 153, 0.07), transparent 50%),' +
            'radial-gradient(ellipse at 20% 80%, rgba(56, 189, 248, 0.05), transparent 50%)',
        }}
      />
      {/* Star dust */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            'radial-gradient(1px 1px at 23% 18%, white, transparent),' +
            'radial-gradient(1px 1px at 71% 33%, white, transparent),' +
            'radial-gradient(1px 1px at 12% 67%, white, transparent),' +
            'radial-gradient(1px 1px at 84% 71%, white, transparent),' +
            'radial-gradient(1px 1px at 45% 88%, white, transparent),' +
            'radial-gradient(2px 2px at 55% 12%, white, transparent),' +
            'radial-gradient(1px 1px at 30% 45%, white, transparent),' +
            'radial-gradient(1px 1px at 92% 22%, white, transparent)',
        }}
      />

      <div className={`relative ${hasArtifact ? 'max-w-[1600px]' : 'max-w-5xl'} mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col min-h-screen`}>
        {/* Header - slightly elevated above the artifact panel so the dark
            Monaco editor never visually "swallows" the badge / status pill */}
        <header className="relative z-20 flex items-center justify-between mb-3 px-3 py-2 rounded-2xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-[11px] font-semibold tracking-wider uppercase text-pink-300">
              Cevee · Live interview
              {hasArtifact && (
                <span className="ml-2 text-white/40 normal-case tracking-normal text-[10px]">
                  · {[
                    codingPanelOn && (lang === 'de' ? 'Coding' : 'Coding'),
                    whiteboardPanelOn && (lang === 'de' ? 'Whiteboard' : 'Whiteboard'),
                    numbersPanelOn && (lang === 'de' ? 'Zahlen' : 'Numbers'),
                  ].filter(Boolean).join(' + ')}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {useComposite && (
              <ScreenSharePill
                lang={lang}
                shared={!!screenStream}
              />
            )}
            <ConnectionPill isLive={isLive} isReconnecting={isReconnecting} lang={lang} />
          </div>
        </header>

        {recorder.uploadHealth !== 'ok' && recorder.state === 'recording' && (
          <div className="mb-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-100 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
            {recorder.uploadHealth === 'offline'
              ? (lang === 'de'
                  ? 'Keine Internetverbindung - die Aufnahme wird gepuffert und sendet automatisch weiter, sobald du wieder online bist.'
                  : 'No internet connection - the recording is buffered and will resume uploading automatically once you are back online.')
              : (lang === 'de'
                  ? 'Upload langsam - die Aufnahme wird im Hintergrund weiter versucht. Schließe diesen Tab noch nicht.'
                  : 'Upload is slow - the recording is still being retried in the background. Please don\'t close this tab yet.')}
          </div>
        )}

        {resumedToast && (
          <div className="absolute top-16 right-4 z-30 max-w-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-100 rounded-xl px-3 py-2 shadow-lg backdrop-blur-sm animate-in fade-in">
            <div className="text-xs font-semibold">
              {lang === 'de' ? 'Wieder verbunden' : 'Reconnected'}
            </div>
            <div className="text-[11px] text-emerald-200/90 leading-snug mt-0.5">
              {lang === 'de'
                ? `Dein Fortschritt ist erhalten - die AI macht da weiter, wo ihr aufgehört habt.`
                : `Your progress is intact - the AI is picking up where you left off.`}
            </div>
          </div>
        )}

        {/* Body: split when artifact mode, stacked otherwise */}
        {hasArtifact ? (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 min-h-0">
            {/* Artifact column (left, big). When both panels are enabled we
                show a small tab switcher; otherwise we just render whichever
                one is on. */}
            <div className="relative z-0 isolate min-h-[480px] lg:min-h-0 flex flex-col gap-2">
              {(() => {
                const enabledPanels: { id: ArtifactTab; label: string }[] = [];
                if (codingPanelOn) enabledPanels.push({ id: 'code', label: lang === 'de' ? 'Code' : 'Code' });
                if (whiteboardPanelOn) enabledPanels.push({ id: 'canvas', label: lang === 'de' ? 'Whiteboard' : 'Whiteboard' });
                if (numbersPanelOn) enabledPanels.push({ id: 'numbers', label: lang === 'de' ? 'Zahlen' : 'Numbers' });
                if (notepadPanelOn) enabledPanels.push({ id: 'notepad', label: lang === 'de' ? 'Notizblock' : 'Notepad' });
                if (enabledPanels.length < 2) return null;
                return (
                  <div className="flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-xl p-1 self-start text-[11px] font-medium">
                    {enabledPanels.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setActiveArtifactTab(p.id)}
                        className={[
                          'px-3 py-1.5 rounded-lg transition-colors',
                          activeArtifactTab === p.id
                            ? 'bg-white/[0.12] text-white'
                            : 'text-white/60 hover:text-white/80',
                        ].join(' ')}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <div className="flex-1 min-h-0">
                {activeArtifactTab === 'code' && codingPanelOn ? (
                  <CodingPane
                    lang={lang}
                    disabled={!isLive || ending}
                    onChange={(p) => codeSync.update(p as unknown as Record<string, unknown>)}
                    onEvent={(action, details) => {
                      // Push the freshest code BEFORE the event so the AI's
                      // feedback is about the actual submitted code, not a
                      // throttled/stale snapshot.
                      if (action === 'submit' || action === 'run') {
                        try { codeSync.flush(); } catch { /* noop */ }
                      }
                      sendArtifactEvent('code', action, details);
                    }}
                    lastPushedAt={codeSync.lastPushedAt}
                    pendingEdit={codeSync.pendingEdit}
                  />
                ) : activeArtifactTab === 'canvas' && whiteboardPanelOn ? (
                  <SystemDesignPane
                    lang={lang}
                    disabled={!isLive || ending}
                    onChange={(p) => canvasSync.update(p as unknown as Record<string, unknown>)}
                    onEvent={(action, details) => {
                      if (action === 'submit') {
                        try { canvasSync.flush(); } catch { /* noop */ }
                      }
                      sendArtifactEvent('canvas', action, details);
                    }}
                    lastPushedAt={canvasSync.lastPushedAt}
                    pendingEdit={canvasSync.pendingEdit}
                  />
                ) : activeArtifactTab === 'numbers' && numbersPanelOn ? (
                  <NumbersPane
                    lang={lang}
                    disabled={!isLive || ending}
                    onChange={(p) => numbersSync.update(p as unknown as Record<string, unknown>)}
                    onEvent={(action, details) => {
                      if (action === 'submit') {
                        try { numbersSync.flush(); } catch { /* noop */ }
                      }
                      sendArtifactEvent('numbers', action, details);
                    }}
                    lastPushedAt={numbersSync.lastPushedAt}
                    pendingEdit={numbersSync.pendingEdit}
                  />
                ) : activeArtifactTab === 'notepad' && notepadPanelOn ? (
                  <NotepadPane
                    lang={lang}
                    disabled={!isLive || ending}
                    onChange={(p) => notepadSync.update(p as unknown as Record<string, unknown>)}
                    onEvent={(action, details) => {
                      if (action === 'submit') {
                        try { notepadSync.flush(); } catch { /* noop */ }
                      }
                      sendArtifactEvent('notepad', action, details);
                    }}
                    lastPushedAt={notepadSync.lastPushedAt}
                    pendingEdit={notepadSync.pendingEdit}
                  />
                ) : null}
              </div>
            </div>
            {/* Voice column (right, small) */}
            <div className="flex flex-col items-center justify-start pt-4 lg:pt-2">
              <PersonaBubble
                gradient={personaGradient}
                size={180}
                status={bubbleStatus}
                speaking={speaking}
                amplitude={aiAmplitude}
              />
              <p className="mt-4 text-base font-semibold tracking-tight text-white/95">
                {startResp.persona?.name}
              </p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">
                {speaking
                  ? lang === 'de' ? 'Spricht' : 'Speaking'
                  : candidateSpeaking
                    ? lang === 'de' ? 'Hört dich' : 'Hearing you'
                    : isLive
                      ? lang === 'de' ? 'Hört zu' : 'Listening'
                      : lang === 'de' ? 'Verbindet' : 'Connecting'}
              </p>

              {aiTextLive && (
                <p className="mt-3 text-[11px] text-white/80 leading-relaxed px-2 text-center max-h-24 overflow-y-auto">
                  {aiTextLive}
                </p>
              )}

              {candidateTextLive && (
                <div className="mt-2 flex justify-end">
                  <div className="max-w-[90%] px-2.5 py-1.5 rounded-xl rounded-br-md bg-white/[0.07] text-white/60 border border-white/10 text-[11px] leading-snug italic">
                    {candidateTextLive}
                  </div>
                </div>
              )}

              {transcript.length > 0 && (
                // Newest-on-top order so the user never has to scroll down
                // to catch up: the live AI subtitle is rendered above and
                // finished lines drop in right beneath it. `.slice()` already
                // returns a fresh array, so `.reverse()` is safe here.
                <div className="mt-3 w-full flex-1 overflow-y-auto space-y-1.5 text-xs pr-1">
                  {transcript.slice(-12).reverse().map((line, i) => (
                    <div key={i} className={`flex ${line.speaker === 'candidate' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={
                          line.speaker === 'candidate'
                            ? 'max-w-[90%] px-2.5 py-1.5 rounded-xl rounded-br-md bg-white/[0.07] text-white/85 border border-white/10 text-[11px] leading-snug'
                            : line.speaker === 'system'
                            ? 'max-w-[90%] px-2 py-1.5 rounded-xl bg-amber-500/10 text-amber-200 border border-amber-500/20 text-[10px]'
                            : 'max-w-[90%] px-2.5 py-1.5 rounded-xl rounded-bl-md bg-white/[0.04] text-white/90 border border-white/10 text-[11px] leading-snug'
                        }
                      >
                        {line.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {wsError && (
                <WsErrorBanner
                  lang={lang}
                  code={wsError}
                  size="sm"
                  onRetry={() => { setWsError(null); forceReconnect(); }}
                />
              )}

              <div className="mt-4 flex items-center gap-2">
                <ControlPill
                  active={micOn}
                  onToggle={() => setMicOn((v) => !v)}
                  iconOn={<Mic className="w-3.5 h-3.5" />}
                  iconOff={<MicOff className="w-3.5 h-3.5" />}
                />
                <ControlPill
                  active={camOn}
                  onToggle={() => setCamOn((v) => !v)}
                  iconOn={<Video className="w-3.5 h-3.5" />}
                  iconOff={<VideoOff className="w-3.5 h-3.5" />}
                />
                <button
                  onClick={handleHangup}
                  disabled={ending || hangingUp}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/25 disabled:opacity-60 text-[11px]"
                >
                  {(ending || hangingUp) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneOff className="w-3.5 h-3.5" />}
                  {(ending || hangingUp)
                    ? (lang === 'de' ? 'Beende ...' : 'Ending ...')
                    : (lang === 'de' ? 'Beenden' : 'End')}
                </button>
              </div>
            </div>
          </div>
        ) : (
        <>
        {/* Persona orb (behavioral: full-screen layout) */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <PersonaBubble
            gradient={personaGradient}
            size={320}
            status={bubbleStatus}
            speaking={speaking}
            amplitude={aiAmplitude}
          />
          <p className="mt-8 text-lg font-semibold tracking-tight text-white/95">
            {startResp.persona?.name}
          </p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1.5">
            {speaking
              ? lang === 'de' ? 'Spricht' : 'Speaking'
              : candidateSpeaking
                ? lang === 'de' ? 'Hört dich' : 'Hearing you'
                  : isLive
                    ? lang === 'de' ? 'Hört zu' : 'Listening'
                  : lang === 'de' ? 'Verbinde' : 'Connecting'}
          </p>

          {/* Live subtitle (assistant) while it's still speaking */}
          {aiTextLive && (
            <p className="mt-5 max-w-2xl text-center text-sm text-white/85 leading-relaxed px-6">
              {aiTextLive}
            </p>
          )}

          {/* Live subtitle (candidate's own words) while they're speaking */}
          {candidateTextLive && (
            <p className="mt-3 max-w-2xl text-center text-sm text-white/55 leading-relaxed px-6 italic">
              {candidateTextLive}
            </p>
          )}

          {wsError && (
            <WsErrorBanner
              lang={lang}
              code={wsError}
              size="lg"
              onRetry={() => { setWsError(null); forceReconnect(); }}
            />
          )}
        </div>

        {/* Transcript -- compact glassy strip, newest first so the latest
            exchange stays visible right under the live AI subtitle without
            requiring a scroll. */}
        {transcript.length > 0 && (
          <div className="mt-6 max-h-40 overflow-y-auto space-y-1.5 text-sm scroll-smooth">
            {transcript.slice(-5).reverse().map((line, i) => (
              <div key={i} className={`flex ${line.speaker === 'candidate' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={
                    line.speaker === 'candidate'
                      ? 'max-w-[80%] px-3.5 py-2 rounded-2xl rounded-br-md bg-white/[0.07] backdrop-blur-md text-white/90 border border-white/10'
                      : line.speaker === 'system'
                      ? 'max-w-[80%] px-3 py-2 rounded-2xl bg-amber-500/10 text-amber-200 border border-amber-500/20 text-xs'
                      : 'max-w-[80%] px-3.5 py-2 rounded-2xl rounded-bl-md bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-md text-white/95 border border-white/10'
                  }
                >
                  {line.text}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Composer */}
        <div className="mt-4">
          <div className="flex items-end gap-2 p-2 rounded-2xl bg-white/[0.04] backdrop-blur-md border border-white/10">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              rows={1}
              placeholder={
                lang === 'de'
                  ? 'Antwort tippen … (falls Mikro mal nicht geht)'
                  : 'Type your answer … (fallback if your mic is off)'
              }
              className="flex-1 bg-transparent text-sm text-white/90 placeholder:text-white/30 outline-none resize-none px-3 py-2"
              disabled={!isLive}
            />
            <button
              onClick={handleSubmit}
              disabled={!isLive || !draft.trim()}
              className="px-3 py-2 rounded-full bg-white text-slate-900 hover:bg-pink-100 disabled:bg-white/20 disabled:text-white/40 disabled:cursor-not-allowed transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-white/40">
            <span>
              {lang === 'de'
                ? 'Sprich frei - die KI hört zu. Text-Eingabe ist Fallback.'
                : 'Speak freely - the AI is listening. Typing is the fallback.'}
            </span>
            <div className="flex items-center gap-2">
              <ControlPill
                active={micOn}
                onToggle={() => setMicOn((v) => !v)}
                iconOn={<Mic className="w-3.5 h-3.5" />}
                iconOff={<MicOff className="w-3.5 h-3.5" />}
              />
              <ControlPill
                active={camOn}
                onToggle={() => setCamOn((v) => !v)}
                iconOn={<Video className="w-3.5 h-3.5" />}
                iconOff={<VideoOff className="w-3.5 h-3.5" />}
              />
              <button
                onClick={handleHangup}
                disabled={ending || hangingUp}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/25 disabled:opacity-60"
              >
                {(ending || hangingUp) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneOff className="w-3.5 h-3.5" />}
                {(ending || hangingUp)
                  ? (lang === 'de' ? 'Beende ...' : 'Ending ...')
                  : (lang === 'de' ? 'Beenden' : 'End')}
              </button>
            </div>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Self-view: floating picture-in-picture */}
      <div className="fixed bottom-5 right-5 z-30 select-none">
        <div className="relative w-40 sm:w-56 aspect-video rounded-2xl overflow-hidden bg-black/60 border border-white/15 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.6)] backdrop-blur">
          {stream ? (
            <>
              <video
                ref={selfVideoRef}
                muted
                playsInline
                autoPlay
                className={`w-full h-full object-cover ${camOn ? '' : 'opacity-0'}`}
                style={{ transform: 'scaleX(-1)' }}
              />
              {!camOn && (
                <div className="absolute inset-0 flex items-center justify-center text-white/40 bg-black/60">
                  <VideoOff className="w-5 h-5" />
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/40">
              <VideoOff className="w-5 h-5" />
            </div>
          )}
          <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-white/80 bg-black/40 backdrop-blur px-1.5 py-0.5 rounded">
              {lang === 'de' ? 'Du' : 'You'}
            </span>
            {!micOn && (
              <span className="bg-red-500/80 text-white text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                <MicOff className="w-2.5 h-2.5" /> mute
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Full-screen blocking overlay while the recorder drains its final
          MediaRecorder cluster + uploads the trailing chunks + calls
          /recording/finalize. This whole pipeline can take 30-60s on
          slow uplinks, and if the candidate closes the tab during it
          the WebM gets a truncated trailing cluster and the replay
          falls back to first-segment-only (1-2 min instead of full
          length). The blocking modal makes the wait explicit so they
          don't bail. We also keep the beforeunload guard active
          during this window (see effect above) as a belt-and-braces
          fallback for users who try to close anyway. */}
      {(ending || hangingUp) && (
        <EndingOverlay lang={lang} />
      )}
    </div>
  );
};

const EndingOverlay: React.FC<{ lang: 'de' | 'en' }> = ({ lang }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-md px-6"
    role="alertdialog"
    aria-modal="true"
    aria-live="assertive"
  >
    <div className="max-w-md w-full rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-7 text-center shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500/20 to-orange-400/20 border border-pink-300/20 flex items-center justify-center mb-5">
        <Loader2 className="w-7 h-7 text-pink-200 animate-spin" />
      </div>
      <h2 className="text-lg font-semibold text-white">
        {lang === 'de'
          ? 'Interview wird abgeschlossen ...'
          : 'Wrapping up your interview ...'}
      </h2>
      <p className="mt-2 text-sm text-white/70 leading-relaxed">
        {lang === 'de'
          ? 'Wir laden gerade deine Aufnahme hoch und schließen die Session sauber ab. Das kann bis zu einer Minute dauern.'
          : 'We\'re uploading your recording and closing the session cleanly. This can take up to a minute.'}
      </p>
      <div className="mt-5 rounded-2xl border border-amber-300/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] text-amber-100 flex items-start gap-2 text-left">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          {lang === 'de'
            ? <>Bitte <strong className="font-semibold">schließe dieses Fenster nicht</strong> und lade die Seite nicht neu.</>
            : <>Please <strong className="font-semibold">don't close this window</strong> or reload the page.</>}
        </span>
      </div>
    </div>
  </div>
);

const ControlPill: React.FC<{
  active: boolean;
  onToggle: () => void;
  iconOn: React.ReactNode;
  iconOff: React.ReactNode;
}> = ({ active, onToggle, iconOn, iconOff }) => (
  <button
    onClick={onToggle}
    className={
      active
        ? 'inline-flex items-center justify-center w-8 h-8 rounded-xl bg-white/[0.08] border border-white/15 text-white hover:bg-white/[0.12]'
        : 'inline-flex items-center justify-center w-8 h-8 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30'
    }
    aria-pressed={active}
  >
    {active ? iconOn : iconOff}
  </button>
);

// Banner shown when the WS layer surfaces an error. For
// `reconnect_giveup` we render a friendly retry CTA -- after 7 failed
// reconnect attempts (~2 min) the auto-loop has given up and the
// candidate needs an explicit nudge to try once more before reloading
// the whole page (which would tear down recording mid-flight).
const WsErrorBanner: React.FC<{
  lang: 'de' | 'en';
  code: string;
  size: 'sm' | 'lg';
  onRetry: () => void;
}> = ({ lang, code, size, onRetry }) => {
  const isGiveup = code === 'reconnect_giveup';
  const small = size === 'sm';
  const wrap = small
    ? 'mt-3 flex items-start gap-2 px-2.5 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-200'
    : 'mt-6 flex items-start gap-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-200';
  const btn = small
    ? 'ml-2 px-2 py-0.5 rounded-md bg-red-500/30 hover:bg-red-500/50 text-red-50 text-[11px] font-semibold'
    : 'ml-3 px-2.5 py-1 rounded-lg bg-red-500/30 hover:bg-red-500/50 text-red-50 text-xs font-semibold';
  const message = isGiveup
    ? (lang === 'de'
        ? 'Verbindung verloren. Wir haben mehrfach versucht neu zu verbinden.'
        : 'Connection lost. We tried to reconnect several times.')
    : code;
  return (
    <div className={wrap}>
      <AlertTriangle className={small ? 'w-3.5 h-3.5 mt-0.5 flex-shrink-0' : 'w-4 h-4 mt-0.5 flex-shrink-0'} />
      <span className="flex-1">{message}</span>
      {isGiveup && (
        <button type="button" onClick={onRetry} className={btn}>
          {lang === 'de' ? 'Neu verbinden' : 'Reconnect'}
        </button>
      )}
    </div>
  );
};

// ============================================================
// Utility screens
// ============================================================

const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50/30 text-slate-500">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-7 h-7 animate-spin" />
      <p className="text-sm">Loading session ...</p>
    </div>
  </div>
);

const ErrorScreen: React.FC<{ lang: 'de' | 'en'; message: string; onBack: () => void }> = ({
  lang,
  message,
  onBack,
}) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50/30 px-6">
    <div className="max-w-md w-full rounded-3xl bg-white border border-red-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center text-red-600 flex-shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">
            {lang === 'de' ? 'Etwas ist schiefgelaufen' : 'Something went wrong'}
          </h3>
          <p className="text-sm text-slate-600 mt-1 break-words">{message}</p>
          <button
            onClick={onBack}
            className="mt-4 px-3 py-2 text-sm font-semibold text-slate-700 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
          >
            {lang === 'de' ? 'Zurück' : 'Back'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================
// AbandonedScreen
// ----
// Shown when the candidate lands on /interview/room/<id> but the
// session is already `in_progress` (they were inside the call once,
// then closed/refreshed the tab). We deliberately do NOT offer a
// resume button:
//   * the AI's Realtime context is gone (each Azure session is a
//     fresh socket; the in-memory turn history can't be re-attached
//     after a hard refresh), so the AI would restart from scratch
//     anyway -- giving a confusing "did the last 10 minutes count?"
//     experience.
//   * we don't want candidates to "rewind" weak answers by closing
//     the tab and re-entering; that would undermine the interview's
//     integrity for the recruiter.
// Instead we surface a clean explainer + a way to act (mock: back to
// history so they can start a new simulation against their quota;
// recruiter: contact the company for a fresh invite).
// ============================================================
const AbandonedScreen: React.FC<{
  lang: 'de' | 'en';
  isMock: boolean;
  isPreScreen?: boolean;
  isRecruiterDemo?: boolean;
  onMockHistory: () => void;
  onRecruiterHub: () => void;
}> = ({ lang, isMock, isPreScreen = false, isRecruiterDemo = false, onMockHistory, onRecruiterHub }) => {
  const t = {
    title: lang === 'de'
      ? 'Dieses Interview wurde unterbrochen'
      : 'This interview was interrupted',
    sub: lang === 'de'
      ? 'Die Verbindung zum Gespräch wurde unterbrochen - der Tab wurde geschlossen, die Seite neu geladen oder das Internet war kurz weg. Aus Fairness-Gründen können wir die Session nicht automatisch fortsetzen.'
      : 'The connection to the conversation was interrupted - the tab was closed, the page reloaded, or your internet dropped briefly. For fairness reasons we can\'t resume the session automatically.',
    whyTitle: lang === 'de' ? 'Gut zu wissen' : 'Good to know',
    why1: lang === 'de'
      ? 'Jedes Interview läuft aus Fairnessgründen in einem zusammenhängenden Durchgang - so ist es für alle gleich und vergleichbar.'
      : 'For fairness, every interview runs as a single continuous session - so it stays consistent and comparable for everyone.',
    mockCta: lang === 'de' ? 'Neue Simulation starten' : 'Start a new simulation',
    mockHint: lang === 'de'
      ? 'Du wirst zur Übersicht weitergeleitet, dort kannst du jederzeit ein neues Mock-Interview beginnen.'
      : 'You\'ll be taken to your history page where you can start a new mock interview any time.',
    prescreenCta: lang === 'de' ? 'Zurück zum Passport' : 'Back to your Passport',
    prescreenHint: lang === 'de'
      ? 'Kein Problem - im Intro-Tab deines Passports kannst du das Intro einfach neu starten (im Rahmen deiner verfügbaren Versuche).'
      : 'No worries - in the Intro tab of your Passport you can simply start the Intro again (within your available attempts).',
    demoCta: lang === 'de' ? 'Zurück zum Interview-Hub' : 'Back to interview hub',
    demoHint: lang === 'de'
      ? 'Dein Demo-Durchlauf erscheint im Hub unter Ergebnisse, sobald die Aufnahme verarbeitet ist.'
      : 'Your demo run will appear in the hub under Results once processing finishes.',
    recruiterCta: lang === 'de' ? 'Recruiter:in kontaktieren' : 'Contact recruiter',
    recruiterHint: lang === 'de'
      ? 'Kein Problem - melde dich kurz bei deinem Recruiting-Team oder antworte einfach auf die Einladungs-E-Mail, dann bekommst du einen neuen Interview-Link.'
      : 'No worries - just reach out to your recruiting team or reply to the invitation email, and they\'ll send you a fresh interview link.',
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50/30 px-6 py-10">
      <div className="max-w-lg w-full rounded-3xl bg-white border border-slate-200/70 p-7 shadow-[0_2px_12px_rgba(15,23,42,0.06)]">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 flex-shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">{t.title}</h1>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{t.sub}</p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t.whyTitle}</p>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
            <li className="flex gap-2"><span className="text-slate-400 mt-0.5">·</span><span>{t.why1}</span></li>
          </ul>
        </div>

        {isRecruiterDemo ? (
          <div className="mt-5">
            <button
              onClick={onRecruiterHub}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {t.demoCta}
            </button>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t.demoHint}</p>
          </div>
        ) : isPreScreen ? (
          <div className="mt-5">
            <button
              onClick={onMockHistory}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {t.prescreenCta}
            </button>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t.prescreenHint}</p>
          </div>
        ) : isMock ? (
          <div className="mt-5">
            <button
              onClick={onMockHistory}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {t.mockCta}
            </button>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t.mockHint}</p>
          </div>
        ) : (
          <div className="mt-5">
            <a
              href="mailto:lea.anders@cevee.eu"
              className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {t.recruiterCta}
            </a>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t.recruiterHint}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ThankYouScreen: React.FC<{
  lang: 'de' | 'en';
  isMock?: boolean;
  isPreScreen?: boolean;
  isRecruiterDemo?: boolean;
  onBack: () => void;
}> = ({ lang, isMock = false, isPreScreen = false, isRecruiterDemo = false, onBack }) => {
  // Recruiter demo: replay + scorecard live in the interview hub, not
  // the consumer mock history (demos are filtered out of /mock/history).
  const tDemo = {
    title: lang === 'de' ? 'Demo abgeschlossen' : 'Demo complete',
    intro: lang === 'de'
      ? 'Dein Demo-Durchlauf ist gespeichert. Was jetzt passiert:'
      : 'Your demo run is saved. Here\'s what happens next:',
    stepsTitle: lang === 'de' ? 'Wie es weitergeht' : 'Next steps',
    step1: lang === 'de'
      ? 'Die KI erstellt die Scorecard aus dem Transcript - meistens innerhalb einer Minute.'
      : 'The AI generates the scorecard from the transcript - usually within a minute.',
    step2: lang === 'de'
      ? 'Video, Transcript und Scorecard erscheinen in deinem Interview-Hub unter Ergebnisse (Demo-Badge).'
      : 'Video, transcript and scorecard appear in your interview hub under Results (Demo badge).',
    step3: lang === 'de'
      ? 'So siehst du, was deine Kandidat:innen nach dem Interview erleben.'
      : 'This shows you what your candidates will see after their interview.',
    rightsTitle: lang === 'de' ? 'Hinweis' : 'Note',
    rights: lang === 'de'
      ? 'Das ist eine interne Vorschau - keine Einladung wurde versendet.'
      : 'This is an internal preview - no invite was sent.',
    privacy: lang === 'de' ? 'Datenschutz' : 'Privacy notice',
    terms:   lang === 'de' ? 'AGB' : 'Terms',
    imprint: lang === 'de' ? 'Impressum' : 'Imprint',
    close: lang === 'de' ? 'Zum Interview-Hub' : 'Open interview hub',
  };

  // Mock copy: candidate sees scorecard themselves in /mock/history.
  // No recruiter review, no company email -- they are the audience.
  const tMock = {
    title: lang === 'de' ? 'Geschafft - gut gemacht!' : 'All done - great job!',
    intro: lang === 'de'
      ? 'Deine AI-Video-Interview-Simulation ist gespeichert. Was jetzt passiert:'
      : "Your AI video interview simulation is saved. Here's what happens next:",
    stepsTitle: lang === 'de' ? 'Wie es weitergeht' : 'Next steps',
    step1: lang === 'de'
      ? 'Die KI erstellt deine Scorecard aus dem Transcript - meistens innerhalb einer Minute.'
      : 'The AI generates your scorecard from the transcript - usually within a minute.',
    step2: lang === 'de'
      ? 'Video, Transcript und Scorecard landen in deinem Simulations-Verlauf. Nur du siehst sie - keine Recruiter:in, keine Drittpartei.'
      : 'Your video, transcript and scorecard appear in your simulation history. Only you can see them - no recruiter, no third party.',
    step3: lang === 'de'
      ? 'Aufbewahrung: 90 Tage. Du kannst jede Simulation jederzeit selbst löschen.'
      : 'Retention: 90 days. You can delete any simulation yourself at any time.',
    rightsTitle: lang === 'de' ? 'Deine Rechte' : 'Your rights',
    rights: lang === 'de'
      ? 'Du kannst jederzeit Löschung, Auskunft oder Berichtigung deiner Simulations-Daten anfragen. Schreib einfach an'
      : 'You can request deletion, access or correction of your simulation data at any time. Just email',
    privacy: lang === 'de' ? 'Datenschutz' : 'Privacy notice',
    terms:   lang === 'de' ? 'AGB' : 'Terms',
    imprint: lang === 'de' ? 'Impressum' : 'Imprint',
    close: lang === 'de' ? 'Zum Verlauf' : 'Open history',
  };

  // Intro copy: candidate runs it on themselves from the Passport and
  // CHOOSES whether recruiters see it. NOT a private practice simulation.
  const tPrescreen = {
    title: lang === 'de' ? 'Geschafft - gut gemacht!' : 'All done - great job!',
    intro: lang === 'de'
      ? 'Dein Cevee Intro ist gespeichert. Was jetzt passiert:'
      : "Your Cevee Intro is saved. Here's what happens next:",
    stepsTitle: lang === 'de' ? 'Wie es weitergeht' : 'Next steps',
    step1: lang === 'de'
      ? 'Die KI erstellt deine Scorecard aus dem Transcript - meistens innerhalb einer Minute.'
      : 'The AI generates your scorecard from the transcript - usually within a minute.',
    step2: lang === 'de'
      ? 'Ergebnis, Transcript und Video landen im Intro-Tab deines Passports. Standardmäßig privat - Recruiter:innen sehen es nur, wenn du die Sichtbarkeit selbst freigibst.'
      : 'Your result, transcript and video appear in the Intro tab of your Passport. Private by default - recruiters only see it if you turn on visibility yourself.',
    step3: lang === 'de'
      ? 'Du hast 3 Versuche pro Typ in 90 Tagen und kannst jeden Versuch jederzeit löschen.'
      : 'You get 3 attempts per type in 90 days and can delete any attempt at any time.',
    rightsTitle: lang === 'de' ? 'Deine Rechte' : 'Your rights',
    rights: lang === 'de'
      ? 'Du kannst jederzeit Löschung, Auskunft oder Berichtigung deiner Intro-Daten anfragen. Schreib einfach an'
      : 'You can request deletion, access or correction of your Intro data at any time. Just email',
    privacy: lang === 'de' ? 'Datenschutz' : 'Privacy notice',
    terms:   lang === 'de' ? 'AGB' : 'Terms',
    imprint: lang === 'de' ? 'Impressum' : 'Imprint',
    close: lang === 'de' ? 'Zum Passport' : 'Open Passport',
  };

  const tRecruiter = {
    title: lang === 'de' ? 'Geschafft - danke für dein Interview' : 'All done - thank you',
    intro: lang === 'de'
      ? 'Dein Interview ist gespeichert. Was jetzt passiert:'
      : "Your interview is saved. Here's what happens next:",
    stepsTitle: lang === 'de' ? 'Wie es weitergeht' : 'Next steps',
    step1: lang === 'de'
      ? 'Eine Recruiterin oder ein Recruiter prüft dein Interview und entscheidet über die nächsten Schritte (immer eine menschliche Entscheidung, DSGVO Art. 22).'
      : 'A recruiter reviews your interview and decides on the next steps (always a human decision, GDPR Art. 22).',
    step2: lang === 'de'
      ? 'Unsere KI hilft beim Review, indem sie deine Antworten zusammenfasst - das ist nur eine Hilfestellung, keine Entscheidung.'
      : 'Our AI assists the review by summarising your answers - this is only an aid, never the decision.',
    step3: lang === 'de'
      ? 'Das Unternehmen meldet sich per Mail mit dem Ergebnis - meistens innerhalb weniger Tage.'
      : 'The company will email you with the outcome, usually within a few days.',
    rightsTitle: lang === 'de' ? 'Deine Rechte' : 'Your rights',
    rights: lang === 'de'
      ? 'Du kannst jederzeit Löschung, Auskunft oder Berichtigung deiner Interview-Daten anfragen. Schreib einfach an'
      : 'You can request deletion, access or correction of your interview data at any time. Just email',
    privacy: lang === 'de' ? 'Datenschutz' : 'Privacy notice',
    terms:   lang === 'de' ? 'AGB' : 'Terms',
    imprint: lang === 'de' ? 'Impressum' : 'Imprint',
    canClose: lang === 'de'
      ? 'Du kannst dieses Fenster jetzt schließen.'
      : 'You can close this window now.',
  };

  const t = isRecruiterDemo ? tDemo : isPreScreen ? tPrescreen : isMock ? tMock : tRecruiter;
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50/30 px-6 py-10">
      <div className="max-w-xl w-full rounded-3xl bg-white border border-emerald-200/70 shadow-[0_2px_12px_rgba(15,23,42,0.05)] overflow-hidden">
        <div className="px-7 pt-7 pb-5 text-center border-b border-slate-100">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 mx-auto flex items-center justify-center text-emerald-500">
            <Sparkles className="w-7 h-7" />
          </div>
          <h3 className="font-semibold text-slate-900 mt-4 text-lg tracking-tight">{t.title}</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.intro}</p>
        </div>

        <div className="px-7 py-6 space-y-5 text-left">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">{t.stepsTitle}</p>
            <ol className="space-y-2.5">
              {[t.step1, t.step2, t.step3].map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-2xl bg-slate-50/70 border border-slate-200/60 px-4 py-3">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">{t.rightsTitle}</p>
            <p className="text-xs text-slate-600 leading-relaxed">
              {t.rights}{' '}
              <a href="mailto:privacy@cevee.eu" className="text-pink-600 hover:underline">privacy@cevee.eu</a>
            </p>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
              <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-slate-900 underline-offset-2 hover:underline">{t.privacy}</a>
              <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-slate-900 underline-offset-2 hover:underline">{t.terms}</a>
              <a href="/imprint" target="_blank" rel="noreferrer" className="hover:text-slate-900 underline-offset-2 hover:underline">{t.imprint}</a>
            </div>
          </div>
        </div>

        <div className="px-7 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-end">
          {isRecruiterDemo ? (
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {tDemo.close}
            </button>
          ) : isPreScreen ? (
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {tPrescreen.close}
            </button>
          ) : isMock ? (
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              {tMock.close}
            </button>
          ) : (
            // window.close() only works on tabs opened by script, and the
            // invite email opens the tab via a normal anchor click -- so a
            // "Close window" button silently no-ops for every invite
            // candidate and just looks broken. Replace it with a plain
            // instructional line so the screen has a clear ending state.
            <p className="text-xs text-slate-500">{tRecruiter.canClose}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; accent: React.ReactNode }> = ({
  label,
  value,
  accent,
}) => (
  <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50/60 border border-slate-100">
    <div className="flex-shrink-0">{accent}</div>
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  </div>
);

// Small pill + popover that shows "screen is being recorded" and
// reassures the candidate that they can swap which feed is big when
// reviewing the playback. With dual-stream recording the LIVE feed
// isn't composited any more, so there's no layout dial here -- the
// reviewer (and the candidate, on mock) chooses focus at playback time.
const ScreenSharePill: React.FC<{
  lang: 'de' | 'en';
  shared: boolean;
}> = ({ lang, shared }) => {
  const [open, setOpen] = useState(false);
  const labels = {
    sharedTxt: lang === 'de' ? 'Bildschirm' : 'Screen',
    noShared: lang === 'de' ? 'Kein Bildschirm' : 'No screen',
    helpShared: lang === 'de'
      ? 'Webcam und Bildschirm werden getrennt aufgenommen. In der Wiedergabe kannst du jederzeit umschalten, welches Bild groß ist.'
      : 'Webcam and screen are recorded as separate tracks. In the playback you can swap which one is the main feed at any time.',
    helpNotShared: lang === 'de'
      ? 'Aktuell ohne Bildschirm. Die Aufnahme zeigt nur dein Webcam-Bild.'
      : 'No screen currently shared. Recording shows webcam only.',
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors',
          shared
            ? 'bg-pink-500/15 border border-pink-400/40 text-pink-200 hover:bg-pink-500/25'
            : 'bg-white/[0.06] border border-white/15 text-white/60 hover:bg-white/[0.1]',
        ].join(' ')}
        title={lang === 'de' ? 'Bildschirm wird aufgezeichnet' : 'Screen is being recorded'}
      >
        <span className={shared ? 'w-1.5 h-1.5 rounded-full bg-pink-300 animate-pulse' : 'w-1.5 h-1.5 rounded-full bg-white/40'} />
        {shared ? labels.sharedTxt : labels.noShared}
        <ChevronDown className={['w-3 h-3 transition-transform', open ? 'rotate-180' : ''].join(' ')} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 rounded-2xl bg-slate-900/95 backdrop-blur border border-white/10 shadow-xl p-3 z-30">
          <p className="text-[11px] text-white/70 leading-relaxed">
            {shared ? labels.helpShared : labels.helpNotShared}
          </p>
        </div>
      )}
    </div>
  );
};

const ConnectionPill: React.FC<{
  isLive: boolean;
  isReconnecting: boolean;
  lang: 'de' | 'en';
}> = ({ isLive, isReconnecting, lang }) => {
  if (isLive) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Live
      </span>
    );
  }
  if (isReconnecting) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-200 text-[11px] font-semibold">
        <Wifi className="w-3 h-3 animate-pulse" />
        {lang === 'de' ? 'Verbinde ...' : 'Connecting ...'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-200 text-[11px] font-semibold">
      <WifiOff className="w-3 h-3" />
      {lang === 'de' ? 'Offline' : 'Offline'}
    </span>
  );
};

export default InterviewRoom;
